import axios from 'axios';
import { API_URL } from '../config/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Subscribe to newsletter (Public)
export const subscribeToNewsletter = async (email, name = null, source = 'footer') => {
  try {
    const response = await api.post('/newsletter/subscribe', { email, name, source });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to subscribe' };
  }
};

// Unsubscribe from newsletter (Public)
export const unsubscribeFromNewsletter = async (email) => {
  try {
    const response = await api.post('/newsletter/unsubscribe', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to unsubscribe' };
  }
};

// Get all subscribers (Admin)
export const getNewsletterSubscribers = async (params = {}) => {
  try {
    const response = await api.get('/newsletter', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch subscribers' };
  }
};

// Get subscriber stats (Admin)
export const getNewsletterStats = async () => {
  try {
    const response = await api.get('/newsletter/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch stats' };
  }
};

// Delete subscriber (Admin)
export const deleteNewsletterSubscriber = async (id) => {
  try {
    const response = await api.delete(`/newsletter/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to delete subscriber' };
  }
};

// Update subscriber (Admin)
export const updateNewsletterSubscriber = async (id, data) => {
  try {
    const response = await api.put(`/newsletter/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update subscriber' };
  }
};

// Export subscribers as CSV (Admin)
export const exportNewsletterSubscribers = async (status = 'all') => {
  try {
    const response = await api.get('/newsletter/export', {
      params: { status },
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'newsletter_subscribers.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    throw error.response?.data || { error: 'Failed to export subscribers' };
  }
};

// Send email to a subscriber (Admin)
export const sendSubscriberEmail = async ({ recipientEmail, recipientName, subject, message }) => {
  try {
    const response = await api.post('/newsletter/send-email', { recipientEmail, recipientName, subject, message });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to send email' };
  }
};

// Send bulk email to multiple subscribers (Admin)
export const sendBulkSubscriberEmail = async ({ recipients, subject, message }) => {
  try {
    const response = await api.post('/newsletter/send-bulk-email', { recipients, subject, message });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to send bulk email' };
  }
};

// Get email logs/history (Admin)
export const getEmailLogs = async (params = {}) => {
  try {
    const response = await api.get('/newsletter/email-logs', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch email logs' };
  }
};

export default {
  subscribeToNewsletter,
  unsubscribeFromNewsletter,
  getNewsletterSubscribers,
  getNewsletterStats,
  deleteNewsletterSubscriber,
  updateNewsletterSubscriber,
  exportNewsletterSubscribers,
  sendSubscriberEmail,
  sendBulkSubscriberEmail,
  getEmailLogs
};
